import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Users,
  Plus,
  Trash2,
  Edit,
  UserPlus,
  Search
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserGroupsPage = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    member_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/user-groups`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setGroups(groupsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      await axios.post(`${API}/user-groups`, newGroup, { withCredentials: true });
      toast.success("Group created successfully!");
      setShowCreate(false);
      setNewGroup({ name: '', description: '', member_ids: [] });
      fetchData();
    } catch (error) {
      toast.error("Failed to create group");
    }
  };

  const handleUpdateGroup = async () => {
    if (!showEdit) return;
    
    try {
      await axios.put(`${API}/user-groups/${showEdit.group_id}`, {
        name: showEdit.name,
        description: showEdit.description,
        member_ids: showEdit.member_ids
      }, { withCredentials: true });
      toast.success("Group updated successfully!");
      setShowEdit(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update group");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm("Are you sure you want to delete this group?")) return;
    
    try {
      await axios.delete(`${API}/user-groups/${groupId}`, { withCredentials: true });
      toast.success("Group deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const toggleMember = (memberId, isEdit = false) => {
    if (isEdit && showEdit) {
      const members = showEdit.member_ids || [];
      if (members.includes(memberId)) {
        setShowEdit({ ...showEdit, member_ids: members.filter(id => id !== memberId) });
      } else {
        setShowEdit({ ...showEdit, member_ids: [...members, memberId] });
      }
    } else {
      const members = newGroup.member_ids;
      if (members.includes(memberId)) {
        setNewGroup({ ...newGroup, member_ids: members.filter(id => id !== memberId) });
      } else {
        setNewGroup({ ...newGroup, member_ids: [...members, memberId] });
      }
    }
  };

  const filteredGroups = groups.filter(g =>
    g.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="user-groups-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              User Groups
            </h1>
            <p className="text-muted-foreground mt-1">
              Organize employees into groups for easier assignment management.
            </p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-group-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Group
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create User Group</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="group-name">Group Name</Label>
                  <Input
                    id="group-name"
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    placeholder="e.g., Night Shift Team"
                    data-testid="group-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="group-desc">Description (Optional)</Label>
                  <Input
                    id="group-desc"
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                    placeholder="Brief description..."
                  />
                </div>
                <div>
                  <Label>Select Members</Label>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 mt-1.5 space-y-2">
                    {employees.map((emp) => (
                      <div key={emp.employee_id} className="flex items-center gap-2">
                        <Checkbox
                          checked={newGroup.member_ids.includes(emp.employee_id)}
                          onCheckedChange={() => toggleMember(emp.employee_id)}
                        />
                        <span className="text-sm">{emp.name}</span>
                        {emp.department && (
                          <span className="text-xs text-muted-foreground">({emp.department})</span>
                        )}
                      </div>
                    ))}
                    {employees.length === 0 && (
                      <p className="text-sm text-muted-foreground">No employees found</p>
                    )}
                  </div>
                </div>
                <Button onClick={handleCreateGroup} className="w-full" data-testid="save-group-btn">
                  Create Group
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-groups"
          />
        </div>

        {/* Groups Grid */}
        {filteredGroups.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group) => (
              <Card key={group.group_id} className="card-hover" data-testid={`group-${group.group_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-white">
                      <Users className="w-6 h-6" />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowEdit(group)}
                        data-testid={`edit-group-${group.group_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteGroup(group.group_id)}
                        className="text-destructive"
                        data-testid={`delete-group-${group.group_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-primary mb-1">{group.name}</h3>
                  {group.description && (
                    <p className="text-sm text-muted-foreground mb-3">{group.description}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      <UserPlus className="w-3 h-3 mr-1" />
                      {group.member_ids?.length || 0} members
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-heading text-lg font-semibold text-primary mb-2">
                No Groups Found
              </h3>
              <p className="text-muted-foreground">
                Create user groups to easily assign toolbox talks to multiple employees.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Group</DialogTitle>
            </DialogHeader>
            {showEdit && (
              <div className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="edit-name">Group Name</Label>
                  <Input
                    id="edit-name"
                    value={showEdit.name}
                    onChange={(e) => setShowEdit({ ...showEdit, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-desc">Description</Label>
                  <Input
                    id="edit-desc"
                    value={showEdit.description || ''}
                    onChange={(e) => setShowEdit({ ...showEdit, description: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Members</Label>
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 mt-1.5 space-y-2">
                    {employees.map((emp) => (
                      <div key={emp.employee_id} className="flex items-center gap-2">
                        <Checkbox
                          checked={(showEdit.member_ids || []).includes(emp.employee_id)}
                          onCheckedChange={() => toggleMember(emp.employee_id, true)}
                        />
                        <span className="text-sm">{emp.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={handleUpdateGroup} className="w-full">
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UserGroupsPage;
